import React, {useCallback, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SelectField} from "./FilterComponentStyles";
import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {toTurkishProperCase} from "utils/toTurkishProperCase";
import {cities as cityLocationsData} from "constants/constants";

const CityFilterWithIstanbulContinents = ({
    value,
    onChange,
    onContinentChange = () => {},
    showLabel = true,
    restrictedCities = [],
    showAllOption = true,
    forceShowAllOption = false,
    mode = undefined,
    selectMarginBottom = "20px",
    allowClear = false,
    ignoreCompanyCheck = false,
    placeholder = ""
}) => {
    const {t} = useTranslation();
    const stores = useStore();

    const [loading, cities] = useFetch({
        fetchFn: stores.optionStore.loadCities.bind(null, {}),
        dependencies: [],
        initialCall: true,
        waitForResponse: true,
        condition: true
    });
    const singleCity =
        stores.meStore.auth.cities?.length == 1 ? stores.meStore.auth.cities[0] : null;

    useEffect(() => {
        if (singleCity && value !== singleCity && onChange && !forceShowAllOption) {
            onChange(singleCity);
        }
    }, [singleCity, value, onChange]);

    if (stores.meStore.auth.cities?.length == 1 && !forceShowAllOption) {
        showAllOption = false;
        value = stores.meStore.auth.cities[0];
    }

    const withIstanbulContinents = useCallback(
        (cities = []) => {
            const cityOptions = [];
            (cities || []).forEach(city => {
                if (city.name.toLocaleLowerCase("tr-TR") === "istanbul") {
                    cityOptions.push({
                        ...city,
                        _id: `${city._id?.toString()} ( tümü )`,
                        label: "İstanbul ( Tümü )"
                    });
                    cityOptions.push({
                        ...city,
                        _id: `${city._id?.toString()} ( avrupa )`,
                        label: "İstanbul ( Avrupa )",
                        continent: "avrupa"
                    });
                    cityOptions.push({
                        ...city,
                        _id: `${city._id?.toString()} ( asya )`,
                        label: "İstanbul ( Asya )",
                        continent: "asya"
                    });
                } else {
                    cityOptions.push({...city, label: toTurkishProperCase(city.name)});
                }
            });
            return cityOptions;
        },
        [JSON.stringify(cities)]
    );

    return (
        <React.Fragment>
            {showLabel && <h5>{t("fields.city", {ns: "common"})}</h5>}
            <SelectField
                showSearch
                value={value}
                data-testid="city"
                mode={mode}
                onChange={(value, option) => {
                    onChange(value);
                    onContinentChange?.(option?.["data-continent"]);
                }}
                mb={selectMarginBottom}
                allowClear={allowClear}
                placeholder={placeholder}
                loading={loading}
                filterOption={(input, option) =>
                    option?.["data-label"]
                        ?.toLocaleLowerCase("TR-tr")
                        ?.indexOf(input.toLocaleLowerCase("TR-tr")) >= 0
                }
            >
                {showAllOption && (
                    <SelectField.Option value="all">
                        {t("filters.all", {ns: "common"})}
                    </SelectField.Option>
                )}
                {arrayAlphabeticalSort([...withIstanbulContinents(cities)])
                    .filter(
                        c =>
                            !restrictedCities
                                .map(rC => rC.toLocaleLowerCase("tr-TR"))
                                .includes(c?.name?.toLocaleLowerCase("tr-TR"))
                    )
                    .filter(c =>
                        !stores.meStore.checkRole("isCompanyUser") || ignoreCompanyCheck
                            ? stores.meStore.auth.cities?.length > 0
                                ? stores.meStore.auth.cities.includes(
                                      c?.name?.toLocaleLowerCase("tr-TR")
                                  )
                                : true
                            : stores.meStore.subcompanies
                                  .map(subcompany => subcompany.city)
                                  .includes(c?.name?.toLocaleLowerCase("tr-TR"))
                    )
                    .map((city, i) => (
                        <SelectField.Option
                            data-testid="city_option"
                            key={city._id || i}
                            //value={city?.label?.toLocaleLowerCase("tr-TR")}
                            value={city?._id?.toString()}
                            data-continent={city?.continent}
                            data-center={
                                cityLocationsData?.find(
                                    c => c.value === city.name.toLocaleLowerCase("tr-TR")
                                )?.center
                            }
                            data-label={toTurkishProperCase(city?.label || city?.name)}
                        >
                            {toTurkishProperCase(city?.label || city?.name)}
                        </SelectField.Option>
                    ))}
            </SelectField>
        </React.Fragment>
    );
};

export default CityFilterWithIstanbulContinents;
